import Icon from "@/Components/Icon/Icon";
import { cn } from "@/shared/utils/cn";
import React from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";

interface InterfaceProps {
  onDrageEnd: (result: DropResult) => void;
  droppableId: string;
  list: {
    key: string;
    component: React.ReactNode;
  }[];
  label?: string;
  hint?: string;
  /** "card" renders compact rounded rows for use inside a settings card. */
  variant?: "default" | "card";
}

const DraggableRow = ({
  onDrageEnd,
  list,
  droppableId,
  label,
  hint,
  variant = "default",
}: InterfaceProps) => {
  const isCard = variant === "card";

  return (
    <div>
      {label &&
        (isCard ? (
          <div className="mb-3">
            <div className="text-[1.5rem] font-semibold text-text">{label}</div>
            {hint && (
              <div className="text-small text-text-tertiary">{hint}</div>
            )}
          </div>
        ) : (
          <h2>{label}</h2>
        ))}
      <DragDropContext onDragEnd={onDrageEnd}>
        <Droppable droppableId={droppableId}>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={
                isCard
                  ? "flex flex-col"
                  : "bg-grey bg-[var(--white)] p-[2rem]"
              }
            >
              {list.map((option, i) => (
                <Draggable key={option.key} draggableId={option.key} index={i}>
                  {(provided, { isDragging }) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={cn(isCard && "pb-2")}
                    >
                      {isCard ? (
                        <div
                          className={cn(
                            "flex items-center rounded-xl border bg-white px-4 py-3 transition-shadow",
                            isDragging
                              ? "border-primary shadow-[0_0.6rem_1.6rem_rgba(0,0,0,0.15)]"
                              : "border-grey-blue-light hover:border-border"
                          )}
                        >
                          <span className="mr-2 flex w-8 shrink-0 justify-center text-small font-semibold text-text-tertiary tabular-nums">
                            {i + 1}
                          </span>
                          <div className="flex min-w-0 flex-1 items-center">
                            {option.component}
                          </div>
                          <Icon
                            className="ml-4 size-6 shrink-0 cursor-grab fill-grey-blue"
                            variants="grip"
                          />
                        </div>
                      ) : (
                        <div className="flex border border-dark px-8 py-4 text-center">
                          <div className="mr-8 flex items-center">
                            <Icon
                              className="h-5 w-5 fill-primary"
                              variants="bars"
                            />
                          </div>
                          {option.component}
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided?.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default DraggableRow;
